import { createTheme } from '@mui/material/styles';

// Kitchen-friendly dark theme with amber accent lighting
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FF8A65', // Softer orange for dark mode
      light: '#FFB74D',
      dark: '#F4511E',
      contrastText: '#1A1A1A',
    },
    secondary: {
      main: '#81C784', // Softer green for dark mode
      light: '#A5D6A7',
      dark: '#66BB6A',
      contrastText: '#1A1A1A',
    },
    error: {
      main: '#EF5350',
    },
    warning: {
      main: '#FFB74D', // Warm amber for warnings
    },
    info: {
      main: '#64B5F6',
    },
    success: {
      main: '#81C784',
    },
    background: {
      default: '#1A1A1A', // Very dark but not pure black for kitchen lighting
      paper: '#242424', // Slightly lighter for cards
    },
    text: {
      primary: '#E8E8E8', // High contrast white
      secondary: '#B8B8B8', // Medium contrast gray
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.75rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
      color: '#E8E8E8',
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
      color: '#E8E8E8',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
      color: '#E8E8E8',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: 1.4,
      color: '#E8E8E8',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: 1.4,
      color: '#E8E8E8',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: 1.4,
      color: '#E8E8E8',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.01em',
      color: '#E8E8E8',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.01em',
      color: '#B8B8B8',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '0.02em',
      color: '#B8B8B8',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          padding: '12px 24px',
          fontWeight: 600,
          fontSize: '0.95rem',
          letterSpacing: '0.01em',
        },
        contained: {
          backgroundColor: '#FF8A65',
          color: '#1A1A1A',
          boxShadow: '0px 4px 16px rgba(255, 138, 101, 0.3)',
          '&:hover': {
            backgroundColor: '#FFB74D',
            boxShadow: '0px 6px 24px rgba(255, 138, 101, 0.4)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        },
        outlined: {
          borderColor: '#FF8A65',
          color: '#FF8A65',
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: 'rgba(255, 138, 101, 0.08)',
            boxShadow: '0px 4px 16px rgba(255, 138, 101, 0.15)',
          },
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: '1rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: '#242424',
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 138, 101, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.5)',
            borderColor: 'rgba(255, 138, 101, 0.2)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px',
          '&:last-child': {
            paddingBottom: '20px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#2D2D2D',
            color: '#E8E8E8',
            '& fieldset': {
              borderColor: 'rgba(255, 138, 101, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 138, 101, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderWidth: 2,
              borderColor: '#FF8A65',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#B8B8B8',
            '&.Mui-focused': {
              color: '#FF8A65',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
          backgroundColor: '#242424',
          color: '#E8E8E8',
          borderBottom: '1px solid rgba(255, 138, 101, 0.1)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          color: '#E8E8E8',
          '&:hover': {
            backgroundColor: 'rgba(255, 138, 101, 0.15)',
            color: '#FF8A65',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: '#FF8A65',
          color: '#1A1A1A',
          boxShadow: '0px 8px 28px rgba(255, 138, 101, 0.4)',
          '&:hover': {
            backgroundColor: '#FFB74D',
            boxShadow: '0px 12px 36px rgba(255, 138, 101, 0.5)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#242424',
          color: '#E8E8E8',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 138, 101, 0.2)',
          color: '#E8E8E8',
          border: '1px solid rgba(255, 138, 101, 0.3)',
        },
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
});